/// <amd-module name="TFS/Views/SuiteMePage/SuiteMePageView/ProjectsPivot" />
import React = require("react");

import { ObservableCollection } from "VSS/Core/Observable";
import { ScreenSize, ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { equals } from "VSS/Core/Util/String";

import { Services, VssService } from "VSS/Platform/Context";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssSecurityService } from "VSS/Platform/Security";

import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { FocusGroupContext, IFocusGroupContext } from "VSSUI/FocusGroup";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection } from "VSSUI/FocusZone";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IVssContributedTab } from "VSSUI/Tabs";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { VssPersona } from "VSSUI/VssPersona";
import { ScreenSizeConditional, ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";

import { ITeamProject } from "TFS/Services/TfsProjectService";
import * as SuiteMePageResources from "TFS/Views/SuiteMePage/Resources";
import { SuiteMePageAction, SuiteMePageActionParameterKey } from "TFS/Views/SuiteMePage/SuiteMePageTypes";

import { InstalledProductsComponent } from "./InstalledProducts";

import { IProjectCreateUrlParameters, IProjectsPivotViewSerivce } from "./ProjectsPivotService";

const enum Constants {
    smallCardSize = 300,
    largeCardSize = 440,

    smallWindowWidth = 1440,
    largeWindowWidth = 1820,

    navigationBarWidth = 260,
    projectContentPadding = 64,
    totalProjectCardMargin = 108
}

class ProjectsPivotProviderService extends VssService implements ITabService {
    public getTabs(tabId: string): IVssContributedTab[] {
        const filter = new Filter();

        const commandBarItems = new ObservableCollection<IHeaderCommandBarItem>();

        const securityService = this.pageContext.getService<IVssSecurityService>("IVssSecurityService");
        const canCreateProjects = securityService.hasPermission("3E65F728-F8BC-4ecd-8764-7E378B19BFA7", "NAMESPACE", 4);

        if (canCreateProjects) {
            commandBarItems.push([
                {
                    id: "create",
                    className: "create-project-button",
                    text: SuiteMePageResources.NewProject,
                    iconProps: { iconName: "Add" },
                    onActivate: () => {
                        document.body.dispatchEvent(new CustomEvent("suiteHomeCreateProject", {}));
                    },
                    isPrimary: true
                }
            ]);
        }

        addMenuItems(this.pageContext, commandBarItems, "ms.vss-tfs-web.suite-home-projects-pivot-commands");

        const tab: IVssContributedTab = {
            id: "projects",
            name: SuiteMePageResources.Projects,
            order: 0,
            routeId: "ms.vss-tfs-web.suite-me-page-route",
            commandBarItems: commandBarItems,
            render: () => <ProjectsPivotComponent filter={filter} />,
            renderFilterBar: (inTabBar: boolean) =>
                inTabBar ? (
                    <InlineKeywordFilterBarItem
                        filter={filter}
                        filterItemKey="keyword"
                        maxTextLength={50}
                        placeholder={SuiteMePageResources.FilterProjects}
                    />
                ) : (
                    undefined
                )
        };

        return [tab];
    }
}

Services.add("suite-home-projects-pivot-provider-service", { serviceFactory: ProjectsPivotProviderService });

interface IProjectsPivotComponentProps {
    filter: IFilter;
}

interface IProjectsPivotComponentState {
    searchInProgress: boolean;
    mruProjects: ITeamProject[];
    allMruProjects: ITeamProject[];
    allProjects: ITeamProject[];
    searchResults?: ITeamProject[] | undefined;
    showCreateProjectView?: boolean;
}

class ProjectsPivotComponent extends VssComponent<IProjectsPivotComponentProps, IProjectsPivotComponentState> {
    private static readonly newProjectAction: SuiteMePageAction = "newProject";
    private static readonly actionParameterKey: SuiteMePageActionParameterKey = "_a";
    private static readonly actionParameterKeyAlt: SuiteMePageActionParameterKey = "action";
    private _ttiMarked = false;
    private _initialUrlState: IProjectCreateUrlParameters;

    public constructor(props: IProjectsPivotComponentProps, context: IVssComponentContext) {
        super(props, context);

        const projectsPivotView = context.pageContext.getService<IProjectsPivotViewSerivce>("IProjectsPivotViewSerivce").getProjectsPivotView();

        projectsPivotView.mruProjects.subscribe(() => {
            this.setState({
                allMruProjects: projectsPivotView.mruProjects.value,
                mruProjects: projectsPivotView.mruProjects.value.slice(0, this.getNumberOfCardsToDisplay(projectsPivotView.mruProjects.value.length))
            });
        });

        projectsPivotView.allProjects.subscribe(() => {
            this.setState({
                allProjects: projectsPivotView.allProjects.value
            });
        });

        this._initialUrlState = projectsPivotView.projectCreateUrlParameters;
        const action =
            this._initialUrlState[ProjectsPivotComponent.actionParameterKey] || this._initialUrlState[ProjectsPivotComponent.actionParameterKeyAlt];
        const showCreateProjectView = !!action && (equals(ProjectsPivotComponent.newProjectAction, action, true) || equals("new", action, true));

        this.state = {
            searchInProgress: false,
            allProjects: projectsPivotView.allProjects.value,
            mruProjects: projectsPivotView.mruProjects.value.slice(0, this.getNumberOfCardsToDisplay(projectsPivotView.mruProjects.value.length)),
            allMruProjects: projectsPivotView.mruProjects.value,
            searchResults: undefined,
            showCreateProjectView: showCreateProjectView
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.filter.subscribe(this._onFilterChanged);
        this.addEventListener(document.body, "suiteHomeCreateProject", this._onCreateProjectClick);
        this.addEventListener(window, "resize", this.onResize);

        if (this.state.mruProjects && this.state.mruProjects.length) {
            this.ensureScenarioRecorded();
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this.props.filter.unsubscribe(this._onFilterChanged);
    }

    public render(): JSX.Element {
        const { allProjects, mruProjects, searchInProgress, searchResults, showCreateProjectView } = this.state;

        const projectComponents: JSX.Element[] = [];
        const displayAllProjects = this.state.allProjects.length > this.state.mruProjects.length;

        mruProjects.forEach(project => {
            projectComponents.push(<ProjectCard project={project} key={project.id} />);
        });

        return (
            <>
                {showCreateProjectView && (
                    <WrappedComponent
                        dependencies={["ms.vss-tfs-web.create-project-panel"]}
                        className="page-content page-content-top"
                        wrappedType="createProjectPanel"
                        showPanel={true}
                        invokingSource={this._initialUrlState.source || "SuiteMePage"}
                        processTemplate={this._initialUrlState.processTemplate}
                        versionControlType={this._initialUrlState.versionControl}
                        acquisitionId={this._initialUrlState.acquisitionId}
                        defaultFeatureIds={this._initialUrlState.featureId ? [this._initialUrlState.featureId] : undefined}
                        projectVisibility={this._initialUrlState.projectVisibility}
                        onPanelDismiss={this.onCancelCreateProject}
                    />
                )}
                <div className="page-content flex-column flex-grow flex-noshrink">
                    <div className={css("mru-projects", searchResults && "hidden")}>
                        <ScreenSizeObserver>
                            {(props: { screenSize: ScreenSize }) => (
                                <FocusZone
                                    circularNavigation={true}
                                    direction={FocusZoneDirection.Horizontal}
                                    focusGroupProps={{ defaultElementId: mruProjects.length > 0 ? mruProjects[0].id : undefined }}
                                >
                                    <ul
                                        className={css(
                                            "suite-project-list justify-start",
                                            props.screenSize > ScreenSize.xsmall ? "flex-row" : "flex-column"
                                        )}
                                    >
                                        {projectComponents}
                                    </ul>
                                </FocusZone>
                            )}
                        </ScreenSizeObserver>
                    </div>
                    {!searchInProgress || (searchInProgress && searchResults && searchResults.length) ? (
                        <Card className="no-padding" contentProps={{ contentPadding: false }}>
                            <WrappedComponent
                                dependencies={["ms.vss-tfs-web.suite-me-page-project-list-content"]}
                                wrappedType="tfs-suite-project-list"
                                projects={searchResults || (displayAllProjects ? allProjects : [])}
                            />
                        </Card>
                    ) : (
                        <div className="flex-column flex-grow flex-center justify-center">
                            <div className="font-size-ll font-weight-semibold">{SuiteMePageResources.NoSearchResults}</div>
                            <Button className="reset-search" onClick={this._onClear}>
                                {SuiteMePageResources.ResetSearch}
                            </Button>
                        </div>
                    )}
                </div>
            </>
        );
    }

    public componentDidUpdate(previousProps: IProjectsPivotComponentProps, previousState: IProjectsPivotComponentState) {
        super.componentDidUpdate(previousProps, previousState);
        this.ensureScenarioRecorded();
    }

    private _onCreateProjectClick = (): void => {
        this.setState({ showCreateProjectView: true });
    };

    private _onFilterChanged = (value: IFilterState) => {
        const keywordState = value["keyword"];
        if (keywordState) {
            this._performSearch(keywordState.value);
        }
    };

    private _performSearch = (searchText: string) => {
        let results: ITeamProject[];

        if (searchText) {
            const lowerCaseSearchText = searchText.toLowerCase();

            results = this.state.allProjects.filter(item => {
                return item.name.toLowerCase().indexOf(lowerCaseSearchText) !== -1;
            });

            this.setState({ searchInProgress: true, searchResults: results });
        } else {
            this._onClear();
        }
    };

    private _onClear = () => {
        this.setState({ searchInProgress: false, searchResults: undefined });
        this.props.filter.reset();
    };

    private ensureScenarioRecorded(): void {
        if (!this._ttiMarked) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive("tfs", "WebPlatform.Collection.Home");

            this._ttiMarked = true;
        }
    }

    private onResize = (event: Event) => {
        //  Figure out if we can show more or less mru projects
        const cardsToDisplay = this.getNumberOfCardsToDisplay(this.state.allMruProjects.length);
        if (this.state.mruProjects.length !== cardsToDisplay) {
            const mruProjects = this.state.allMruProjects.slice(0, cardsToDisplay);
            this.setState({ mruProjects });
        }
    };

    private onCancelCreateProject = () => {
        this.setState({
            showCreateProjectView: false
        });
    };

    private getNumberOfCardsToDisplay(numberOfMruProjects: number): number {
        // Based on the window width, we need to figure out how many cards we can show.
        const windowWidth = window.innerWidth;

        // Determine the actual width available after subtracting navbar, and paddings in the content section
        const availableSpace = windowWidth - Constants.totalProjectCardMargin - Constants.projectContentPadding - Constants.navigationBarWidth;

        // We will show between 2 and 4.  We will see if enough room for 4
        const maxPossibleCards = windowWidth > Constants.largeWindowWidth ? 4 : 3;

        // If we have more than 4 mruprojects we will only attempt to display the first 4.
        // If less than 2 mruPrjects, then set requested # to 2.
        let cardsToDisplay = Math.min(Math.max(2, numberOfMruProjects), maxPossibleCards);

        // Figure out card size.  As long as it is larger than the minimum card size,
        // we can display the requested number of cards.
        let cardSize = availableSpace / cardsToDisplay;
        while (cardSize <= Constants.smallCardSize && cardsToDisplay > 2) {
            cardsToDisplay--;
            cardSize = availableSpace / cardsToDisplay;
        }

        if (windowWidth < ScreenBreakpoints.small) {
            cardsToDisplay = 3;
        }
        return cardsToDisplay;
    }
}

interface IProjectCardProps {
    project: ITeamProject;
}

class ProjectCard extends React.Component<IProjectCardProps> {
    private focusGroupContext?: IFocusGroupContext;

    public render(): JSX.Element {
        const { project } = this.props;

        return (
            <FocusWithin onFocus={this.onFocus}>
                {(focusStatus: IFocusWithinStatus) => (
                    <li
                        className={css("project-card flex-row flex-grow", focusStatus.hasFocus && "focused")}
                        onFocus={focusStatus.onFocus}
                        onBlur={focusStatus.onBlur}
                    >
                        <Card className="flex-grow no-padding" contentProps={{ contentPadding: false }}>
                            <FocusGroupContext.Consumer>
                                {focusGroupContext => {
                                    this.focusGroupContext = focusGroupContext;
                                    return (
                                        <FocusZoneContext.Consumer>
                                            {focusZoneContext => (
                                                <a
                                                    className="project-link flex-column flex-grow"
                                                    data-focuszone={focusZoneContext.focuszoneId}
                                                    href={project.url}
                                                    id={project.id}
                                                    tabIndex={focusGroupContext.focusedElementId === project.id ? 0 : -1}
                                                >
                                                    <div className="top-row flex-row flex-noshrink flex-grow">
                                                        <VssPersona
                                                            ariaLabel={null}
                                                            className="project-persona"
                                                            identityDetailsProvider={{
                                                                getDisplayName: () => project.name,
                                                                getIdentityImageUrl: () => project.imageUrl
                                                            }}
                                                            size="large"
                                                        />
                                                        <div className="flex-column project-details">
                                                            <div className="project-name font-size-l font-weight-semibold">{project.name}</div>
                                                            <div className="project-description">{project.description}</div>
                                                        </div>
                                                    </div>
                                                </a>
                                            )}
                                        </FocusZoneContext.Consumer>
                                    );
                                }}
                            </FocusGroupContext.Consumer>
                            <div className="installed-products-row flex-row flex-grow flex-noshrink justify-end">
                                <ScreenSizeConditional condition={screenSize => screenSize > ScreenSize.xsmall}>
                                    <InstalledProductsComponent products={project.products} projectName={project.name} />
                                </ScreenSizeConditional>
                            </div>
                        </Card>
                    </li>
                )}
            </FocusWithin>
        );
    }

    private onFocus = () => {
        if (this.focusGroupContext) {
            this.focusGroupContext.onFocus(this.props.project.id);
        }
    };
}
